import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Workflow as WorkflowIcon, Search, Plus, LayoutGrid, List, User } from 'lucide-react'
import { toast } from 'sonner'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import AppSidebar from '@/components/AppSidebar'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import {
  IntuiSelect,
  IntuiSelectTrigger,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
} from '@/components/intueri/IntuiTabs'
import { IntuiEmptyState } from '@/components/intueri/IntuiEmptyState'
import SquareLoader from './SquareLoader'
import type { Workflow, DisciplineFilter, WorkflowSortKey, WorkflowViewMode } from './types'
import {
  canManageWorkflows,
  deleteWorkflow, duplicateWorkflow, latestRunIdForWorkflow, listWorkflowTemplates, renameWorkflow, selectWorkflows,
} from './workflowData'
import { getWorkflowSettings, updateWorkflowSettings } from './workflowSettings'
import WorkflowCard from './WorkflowCard'
import WorkflowNotificationsBell from './WorkflowNotificationsBell'
import WorkflowTable from './WorkflowTable'

interface DisciplineOption {
  value: DisciplineFilter
  labelKey: string
  defaultLabel: string
}

// The chip row offers the three core disciplines only (with All Workflow /
// My workflow / Templates that's six chips, per the design). The Discipline
// TYPE still carries the full union — workflows tagged with other disciplines
// keep their data and simply aren't chip-filterable here.
const DISCIPLINES: DisciplineOption[] = [
  { value: 'all', labelKey: 'workflow:disciplines.all', defaultLabel: 'All disciplines' },
  { value: 'chemical', labelKey: 'workflow:disciplines.chemical', defaultLabel: 'Chemical' },
  { value: 'electrical', labelKey: 'workflow:disciplines.electrical', defaultLabel: 'Electrical' },
  { value: 'mechanical', labelKey: 'workflow:disciplines.mechanical', defaultLabel: 'Mechanical' },
]

/** A single pill in the discipline filter row (Figma "2. Workflow Selection"). */
function FilterChip({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'border-transparent bg-cyan-700 text-white hover:bg-cyan-800'
          : 'border-border bg-background text-foreground hover:bg-muted dark:bg-dark-card'
      )}
    >
      {icon}
      {children}
    </button>
  )
}

const WorkflowPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAppStore()
  // Annotators are read-only outside the P&ID editor: opening a template or
  // creating a workflow just surfaces the read-only toast (no navigation), so
  // they get feedback at the click instead of after entering a prompt.
  const isAnnotator = isAnnotatorRole(user?.role)
  const openingRef = useRef(false)
  const [search, setSearch] = useState('')
  const [discipline, setDiscipline] = useState<DisciplineFilter>('all')
  // My workflows (tenant-authored) vs all. (The Templates kind chip was
  // removed per the latest Figma — built-ins show under All.)
  const [kind, setKind] = useState<'all' | 'mine'>('all')
  // Rename dialog target (null = closed).
  const [renameTarget, setRenameTarget] = useState<Workflow | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [sortBy, setSortBy] = useState<WorkflowSortKey>('last_used')
  const [viewMode, setViewMode] = useState<WorkflowViewMode>('grid')

  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [loading, setLoading] = useState(true)
  // Per-workflow Active toggle (client-side schedule settings until the
  // scheduler lands server-side) — mirrored into state so toggles re-render.
  const [activeMap, setActiveMap] = useState<Record<string, boolean>>({})
  // Role-gate from the auth store (Keycloak claim) — was a hardcoded 'admin'
  // mock, so Member users saw manage actions the server would reject.
  // Annotators stay excluded regardless of the role mapping (main's rule).
  const canManage = canManageWorkflows(user?.role ?? undefined) && !isAnnotator

  // Real published templates (built-ins + tenant store) via GET .../templates,
  // with a fixtures fallback when the workflow backend is disabled.
  const reload = useCallback(() => {
    let active = true
    setLoading(true)
    listWorkflowTemplates()
      .then((data) => {
        if (!active) return
        setWorkflows([...data])
        setActiveMap(
          Object.fromEntries(data.map((w) => [w.id, getWorkflowSettings(w.id).active]))
        )
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  useEffect(() => reload(), [reload])

  const displayed = useMemo(
    () => selectWorkflows(workflows, { search, discipline, sortBy, kind }),
    [workflows, search, discipline, sortBy, kind],
  )

  const handleCreateWorkflow = () => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    navigate('/workflow/new')
  }
  // Open a workflow: resume its most recent run (graph + chat restored) if one
  // exists, else open the builder on the template id for a fresh run.
  const handleOpenWorkflow = async (w: Workflow) => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    // Guard the async latest-run lookup so rapid double-clicks don't fire
    // multiple navigations.
    if (openingRef.current) return
    openingRef.current = true
    try {
      const runId = await latestRunIdForWorkflow(w.id)
      // Always carry ?templateId (the opened workflow's identity) so Save versions
      // THIS workflow; add ?runId when there's a run to resume (graph + chat).
      const id = encodeURIComponent(w.id)
      navigate(
        runId
          ? `/workflow/new?templateId=${id}&runId=${encodeURIComponent(runId)}`
          : `/workflow/new?templateId=${id}`
      )
    } finally {
      openingRef.current = false
    }
  }

  const handleDuplicate = async (w: Workflow) => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    try {
      await duplicateWorkflow(w.id)
      toast.success(t('workflow:row.duplicated', { defaultValue: 'Workflow duplicated' }))
    } catch {
      toast.error(t('workflow:row.duplicate_failed', { defaultValue: 'Could not duplicate workflow' }))
    }
    reload()
  }
  const handleDelete = async (w: Workflow) => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    try {
      await deleteWorkflow(w.id)
      toast.success(t('workflow:row.deleted', { defaultValue: 'Workflow deleted' }))
    } catch {
      toast.error(t('workflow:row.delete_failed', { defaultValue: 'Could not delete workflow' }))
    }
    reload()
  }
  // Rename via a proper dialog (not window.prompt).
  const handleRename = (w: Workflow) => {
    if (isAnnotator) return notifyAnnotatorBlocked()
    setRenameTarget(w)
    setRenameValue(w.name)
  }
  const confirmRename = async () => {
    const w = renameTarget
    const next = renameValue.trim()
    if (!w || !next || next === w.name) {
      setRenameTarget(null)
      return
    }
    setRenameTarget(null)
    try {
      await renameWorkflow(w.id, next)
      toast.success(t('workflow:row.renamed', { defaultValue: 'Workflow renamed' }))
    } catch {
      toast.error(t('workflow:row.rename_failed', { defaultValue: 'Could not rename workflow' }))
    }
    reload()
  }

  // View Report opens the workflow's Report tab; Test Workflow opens the
  // builder and immediately starts a manual (test) run.
  const handleViewReport = (w: Workflow) =>
    navigate(`/workflow/new?templateId=${encodeURIComponent(w.id)}&view=report`)
  const handleTest = (w: Workflow) =>
    navigate(`/workflow/new?templateId=${encodeURIComponent(w.id)}&test=1`)
  const handleToggleActive = (w: Workflow, active: boolean) => {
    updateWorkflowSettings(w.id, { active })
    setActiveMap((prev) => ({ ...prev, [w.id]: active }))
  }

  const rowActions = {
    onOpen: handleOpenWorkflow,
    onViewReport: handleViewReport,
    onTest: handleTest,
    onDuplicate: handleDuplicate,
    onRename: handleRename,
    onDelete: handleDelete,
  }

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top navbar — Workflow title + Bell */}
        <header className="flex h-[60px] shrink-0 items-center justify-between border-b border-border bg-background px-6 dark:bg-dark-card">
          {/* UI-67: no glyph beside the title — see AppTopbar. */}
          <div className="flex items-center">
            <span className="text-sm font-medium text-foreground">
              {t('nav:menu.workflow', { defaultValue: 'Workflow' })}
            </span>
          </div>
          <WorkflowNotificationsBell workflows={workflows} />
        </header>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-6 overflow-hidden p-6">
          {/* Toolbar — Figma "2. Workflow Selection": search + Create on the top
              row; the discipline filter chip row + sort + view toggle below. */}
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-center justify-between gap-3">
              <div className="relative w-full sm:w-[280px]">
                <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <IntuiInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('workflow:search', { defaultValue: 'Search' })}
                  className="h-9 ps-9"
                />
              </div>

              {/* data-tour-id: help-agent "create-workflow" tour anchor. */}
              <IntuiButton
                size="lg"
                leftIcon={<Plus />}
                onClick={handleCreateWorkflow}
                className="hidden shrink-0 sm:inline-flex"
                data-tour-id="workflow-create"
              >
                {t('workflow:create', { defaultValue: 'Create Workflow' })}
              </IntuiButton>
              <IntuiButton
                size="icon-lg"
                onClick={handleCreateWorkflow}
                aria-label={t('workflow:create', { defaultValue: 'Create Workflow' })}
                className="shrink-0 sm:hidden"
              >
                <Plus className="size-4" />
              </IntuiButton>
            </div>

            <div className="flex w-full flex-wrap items-center justify-between gap-3">
              {/* Filter chips: All Workflows · My workflow · Chemical · Electrical · Mechanical.
                  The WHOLE row is a single-select group — picking any chip clears
                  the others, so "My workflow" can't stay lit once a discipline is
                  chosen (and vice-versa). "My workflow" (kind) and discipline are
                  distinct state axes internally, so each onClick resets the other
                  axis to keep exactly one chip active. */}
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                {/* "All Workflows" is a full reset by design (Figma chip row):
                    it clears BOTH the kind toggle and the discipline chip, so
                    it always lands on the unfiltered list. */}
                <FilterChip
                  active={discipline === 'all' && kind === 'all'}
                  onClick={() => { setDiscipline('all'); setKind('all') }}
                >
                  {t('workflow:filter.all', { defaultValue: 'All Workflows' })}
                </FilterChip>
                <FilterChip
                  active={kind === 'mine'}
                  onClick={() => {
                    // Single-select: selecting "My workflow" clears any discipline;
                    // clicking it again clears back to the unfiltered list.
                    setKind(kind === 'mine' ? 'all' : 'mine')
                    setDiscipline('all')
                  }}
                  icon={<User className="size-3.5" />}
                >
                  {t('workflow:filter.mine', { defaultValue: 'My workflow' })}
                </FilterChip>
                {DISCIPLINES.filter((d) => d.value !== 'all').map((d) => (
                  <FilterChip
                    key={d.value}
                    active={discipline === d.value}
                    onClick={() => {
                      // Single-select: choosing a discipline clears the "My
                      // workflow" kind so only this chip stays active.
                      setDiscipline(discipline === d.value ? 'all' : d.value)
                      setKind('all')
                    }}
                  >
                    {t(d.labelKey, { defaultValue: d.defaultLabel })}
                  </FilterChip>
                ))}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <IntuiSelect value={sortBy} onValueChange={(v) => setSortBy(v as WorkflowSortKey)}>
                  <IntuiSelectTrigger className="h-9 w-[144px]">
                    <IntuiSelectValue />
                  </IntuiSelectTrigger>
                  <IntuiSelectContent>
                    <IntuiSelectItem value="last_used">
                      {t('workflow:sort.last_used', { defaultValue: 'Last used' })}
                    </IntuiSelectItem>
                    <IntuiSelectItem value="newest">
                      {t('workflow:sort.newest', { defaultValue: 'Newest' })}
                    </IntuiSelectItem>
                    <IntuiSelectItem value="name_az">
                      {t('workflow:sort.name_az', { defaultValue: 'Name (A → Z)' })}
                    </IntuiSelectItem>
                    <IntuiSelectItem value="name_za">
                      {t('workflow:sort.name_za', { defaultValue: 'Name (Z → A)' })}
                    </IntuiSelectItem>
                  </IntuiSelectContent>
                </IntuiSelect>

                <IntuiTabs value={viewMode} onValueChange={(v) => setViewMode(v as WorkflowViewMode)}>
                  <IntuiTabsList className="h-9">
                    <IntuiTabsTrigger
                      value="grid"
                      aria-label={t('workflow:view.grid', { defaultValue: 'Grid view' })}
                    >
                      <LayoutGrid className="size-4" />
                    </IntuiTabsTrigger>
                    <IntuiTabsTrigger
                      value="list"
                      aria-label={t('workflow:view.list', { defaultValue: 'List view' })}
                    >
                      <List className="size-4" />
                    </IntuiTabsTrigger>
                  </IntuiTabsList>
                </IntuiTabs>
              </div>
            </div>
          </div>

          {/* Content: loading → empty → grid | table */}
          {loading ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
              <SquareLoader />
              <span className="text-sm text-muted-foreground">
                {t('workflow:loading', { defaultValue: 'Loading workflows...' })}
              </span>
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <IntuiEmptyState
                icon={<WorkflowIcon className="size-4 text-muted-foreground" />}
                title={t('workflow:empty.title', { defaultValue: 'No workflow yet' })}
                description={t('workflow:empty.description', {
                  defaultValue: 'Get started by creating your first workflow',
                })}
                className="w-[308px]"
              />
            </div>
          ) : viewMode === 'grid' ? (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {displayed.map((w) => (
                  <WorkflowCard
                    key={w.id}
                    workflow={w}
                    canManage={canManage}
                    menuHidden={isAnnotator}
                    active={activeMap[w.id] ?? false}
                    onToggleActive={handleToggleActive}
                    {...rowActions}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              {/* Putra ("10. Update" table callout): main-page tables render
                  open — row dividers only, no 4-way outer border. */}
              <WorkflowTable
                workflows={displayed}
                canManage={canManage}
                menuHidden={isAnnotator}
                isActive={(w) => activeMap[w.id] ?? false}
                onToggleActive={handleToggleActive}
                {...rowActions}
              />
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null)
        }}
      >
        <DialogContent className="max-w-md gap-0 rounded-xl p-6">
          <DialogTitle>
            {t('workflow:rename.title', { defaultValue: 'Rename workflow' })}
          </DialogTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void confirmRename()
            }}
            className="mt-4 flex flex-col gap-4"
          >
            <IntuiInput
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder={t('workflow:rename.placeholder', { defaultValue: 'Workflow name' })}
            />
            <div className="flex justify-end gap-2">
              <IntuiButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRenameTarget(null)}
              >
                {t('common:cancel', { defaultValue: 'Cancel' })}
              </IntuiButton>
              <IntuiButton type="submit" size="sm" disabled={!renameValue.trim()}>
                {t('workflow:rename.save', { defaultValue: 'Save' })}
              </IntuiButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default WorkflowPage
